import { useEffect, useState } from 'react';

/**
 * Returns true when the viewport is below the mobile breakpoint (768px).
 *
 * This is the single source of truth for "should we render the mobile
 * shell" — call from the app root to swap shells, and from individual
 * pages that want a different layout per viewport.
 *
 * The breakpoint matches docs/mobile/08-ARCHITECTURE.md (< 768px → mobile).
 * Resize is rare in practice (rotate phone, devtools toggle) — when it
 * happens we re-render, which is fine.
 *
 * SSR-friendly: on the server `window` is undefined, so we initialize to
 * `false` (desktop) and let the client effect correct on first paint.
 * Until we ship SSR there's no hydration mismatch.
 */
export function useMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // Modern API; fall back to addListener for older Safari.
    if ('addEventListener' in mq) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // @ts-expect-error legacy Safari
    mq.addListener(handler);
    return () => {
      // @ts-expect-error legacy Safari
      mq.removeListener(handler);
    };
  }, []);

  return isMobile;
}
